import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  priority?: boolean;
  sizes?: string;
  srcSet?: string;
  fit?: "cover" | "contain";
  onError?: () => void;
  onLoad?: () => void;
}

// Generate WebP and AVIF sources from original image path
const generateModernFormats = (src: string) => {
  const ext = src.split('.').pop()?.toLowerCase();
  const basePath = src.substring(0, src.lastIndexOf('.'));
  
  return {
    avif: ext && ['png', 'jpg', 'jpeg'].includes(ext) ? `${basePath}.avif` : null,
    webp: ext && ['png', 'jpg', 'jpeg'].includes(ext) ? `${basePath}.webp` : null,
    original: src
  };
};

// Generate responsive srcset for different screen sizes
const generateSrcSet = (src: string, width?: number) => {
  if (!width) return undefined;
  
  const ext = src.split('.').pop()?.toLowerCase();
  const basePath = src.substring(0, src.lastIndexOf('.'));
  
  // Generate srcset for 1x, 1.5x, 2x
  return `${src} ${width}w, ${src} ${Math.round(width * 1.5)}w, ${src} ${width * 2}w`;
};

export const LazyImage = ({ 
  src, 
  alt, 
  className, 
  placeholder,
  width,
  height,
  loading = "lazy",
  priority = false,
  sizes,
  srcSet,
  fit = "cover",
  onError,
  onLoad
}: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [loadingState, setLoadingState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formats = generateModernFormats(src);
  const responsiveSrcSet = srcSet || generateSrcSet(src, width);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setLoadingState('loaded');
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    setLoadingState('error');
    onError?.();
  }, [onError]);

  const handleStartLoading = useCallback(() => {
    setLoadingState('loading');
  }, []);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    // Enhanced intersection observer with better performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            handleStartLoading();
            observer.disconnect();
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '100px', // Increased for better UX
        // Use root to improve performance
        root: null
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [priority, handleStartLoading]);

  // Preload critical images
  useEffect(() => {
    if (priority && src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      if (srcSet) {
        link.setAttribute('imagesrcset', srcSet);
      }
      if (sizes) {
        link.setAttribute('imagesizes', sizes);
      }
      document.head.appendChild(link);

      return () => {
        try {
          document.head.removeChild(link);
        } catch (e) {
          // Link might already be removed
        }
      };
    }
  }, [priority, src, srcSet, sizes]);

  return (
    <div ref={containerRef} className={cn("relative overflow-hidden gpu-accelerated", className)}>
      {/* Blur-up placeholder - always shown first */}
      <div 
        className={cn(
          "absolute inset-0 transition-opacity duration-500",
          isLoaded ? "opacity-0" : "opacity-100",
          "bg-gradient-to-br from-muted/50 via-muted/30 to-muted/50",
          "backdrop-blur-xl"
        )}
        style={{
          aspectRatio: width && height ? `${width}/${height}` : "1/1",
        }}
      />
      
      {isInView ? (
        <picture>
          {/* AVIF format - best compression */}
          {formats.avif && (
            <source
              type="image/avif"
              srcSet={responsiveSrcSet?.replace(src, formats.avif)}
              sizes={sizes}
            />
          )}
          
          {/* WebP format - good compression, wide support */}
          {formats.webp && (
            <source
              type="image/webp"
              srcSet={responsiveSrcSet?.replace(src, formats.webp)}
              sizes={sizes}
            />
          )}
          
          {/* Original format - fallback */}
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            width={width}
            height={height}
            sizes={sizes}
            srcSet={responsiveSrcSet}
            loading={priority ? "eager" : loading}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            onLoadStart={handleStartLoading}
            className={cn(
              "w-full h-full transition-all duration-700 ease-out gpu-accelerated",
              fit === "contain" ? "object-contain" : "object-cover",
              isLoaded ? "opacity-100 scale-100 blur-0" : "opacity-0 scale-105 blur-sm",
              hasError ? "bg-muted" : "",
              "will-change-transform will-change-opacity"
            )}
            style={{
              aspectRatio: width && height ? `${width}/${height}` : undefined,
            }}
          />
        </picture>
      ) : (
        // Placeholder while not in view
        <div 
          className={cn(
            "bg-gradient-to-br from-muted via-muted/80 to-muted animate-pulse gpu-accelerated",
            width && height ? "" : "aspect-square"
          )}
          style={{
            aspectRatio: width && height ? `${width}/${height}` : "1/1",
            width: width || '100%',
            height: height || '100%'
          }}
        />
      )}

      {/* Loading State */}
      {loadingState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 backdrop-blur-sm">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground text-sm">
          <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
          <p>Errore nel caricamento</p>
        </div>
      )}
    </div>
  );
};

export default LazyImage;